<template>
    <article class="page-cabinet-dashboard-portfolio">
        <div class="page-cabinet-dashboard-portfolio__headline">
            <div class="page-cabinet-dashboard-portfolio__headline-title">
                Структура портфеля
            </div>
        </div>
        <div class="page-cabinet-dashboard-portfolio__graph">
            <ui-card-base with-border>
                <div class="page-cabinet-dashboard-portfolio__graph-container">
                    <div class="page-cabinet-dashboard-portfolio__graph-legend" v-if="items.length">
                        <div class="page-cabinet-dashboard-portfolio__graph-legend-item _header">
                            <div>Объекты</div>
                            <div>Доля в портфеле</div>
                        </div>
                        <div
                            class="page-cabinet-dashboard-portfolio__graph-legend-item"
                            :class="{'_highlight': focusOnIndex === index}"
                            v-for="(item, index) in slicedItems"
                            :key="item.title"
                            v-on:click="focusOnIndex = focusOnIndex !== index ? index : -1"
                        >
                            <div>{{item.title}}</div>
                            <div>{{item.share}}%</div>
                        </div>

                        <div
                            class="page-cabinet-dashboard-portfolio__graph-legend-pagination"
                            v-if="items.length > perPage"
                        >
                            <page-cabinet-pagination
                                v-bind:current-page="page"
                                v-bind:total-items="items.length"
                                v-bind:per-page="perPage"
                                v-on:change-page="(num) => page = num"
                            />
                        </div>
                    </div>

                    <ui-graph-donut
                        v-bind:series="series"
                        v-bind:labels="labels"
                        v-bind:outsideLabels="investments"
                        v-bind:colors="colors"
                        v-on:hoverTooltip="onHoverTooltip"
                        v-bind:customTooltip="customTooltip"
                    />
                </div>
            </ui-card-base>

            <div class="page-cabinet-dashboard-portfolio__graph-back-forward">
                <page-cabinet-back-forward
                    v-bind:has-prev-button="page > 1"
                    v-bind:has-next-button="hasNextPage"
                    v-on:on-back="page > 1 ? --page : page = 1"
                    v-on:on-forward="page++"
                />
            </div>

            <transition name="fade">
                <div class="page-cabinet-dashboard-portfolio__graph-loader"
                    v-if="getUserSharesIsLoading"
                    >
                    <ui-loader
                        v-bind:fixed="50"
                        v-bind:centered="true"
                    />
                </div>
            </transition>
        </div>
    </article>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'page-cabinet-dashboard-portfolio',
    props: {
        items: {
            type: Array,
            default: null,
        },
    },
    data: function() {
        return {
            page: 1,
            perPage: 5,
            tooltipIndex: -1,
            focusOnIndex: -1,
        };
    },
    computed: {
        ...mapState('dashboard', [
            'getUserSharesIsLoading',
        ]),
        series() {
            return this.items?.map((i) => parseFloat(i.share)) ?? [];
        },
        labels() {
            return this.items?.map((i) => i.title + ' - ' + i.share + '%') ?? [];
        },
        investments() {
            return this.items?.map((i) => [ i.title, i.currentValueFormatted ]) ?? [];
        },
        colors() {
            if (this.focusOnIndex >= 0) {
                return this.items?.map((item, index) => this.focusOnIndex !== index ? '#DFDFDF' : '#6737AF');
            }
            return null;
        },
        customTooltip() {
            if (this.tooltipIndex >= 0) {
                const item = `${this.items[this.tooltipIndex].title}: ${this.items[this.tooltipIndex].currentValueFormatted}`;
                return `<div style="background-color: #27242D; color: white; border: 0; padding: 8px;">${item}</div>`;
            }
            return null;
        },
        startIndex() {
            return (this.page - 1) * this.perPage;
        },
        hasNextPage() {
            return this.startIndex + this.perPage < this.items.length;
        },
        slicedItems() {
            return this.items?.slice(this.startIndex, this.startIndex + this.perPage) ?? [];
        },
    },
    methods: {
        onHoverTooltip(index) {
            this.tooltipIndex = index;
        },
    },
};
</script>

<style scoped lang="less">
@import '~theme';

.page-cabinet-dashboard-portfolio {
    margin-bottom: 40px;
    &__headline {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        width: 100%;
        padding: 0 16px;
        &-title {
            .typography-heading();

            flex: 0 0 auto;
            padding-right: 24px;
            padding-bottom: 8px;
        }
    }

    &__graph {
        position: relative;
        height: 240px;
        &-legend {
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
        }
        @media @media-md-down {
            &-legend {
                width: auto;
            }
        }

        &-legend-item {
            .typography-body();

            display: grid;
            grid-template-columns: minmax(100px, 1fr) minmax(100px, 1fr);
            gap: 24px;
            cursor: pointer;
            padding: 0 4px;
            &._header {
                .typography-caption();

                padding-bottom: 4px;
                color: @color-gray-darker;
            }
            &._highlight {
                border-radius: 4px;
                font-weight: bold;
            }
        }

        &-legend-pagination {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            height: 100%;
        }
        @media @media-md-down {
            &-legend-pagination {
                display: none;
            }
        }

        &-container {
            display: flex;
            flex-direction: row;
            gap: 16px;
            width: 100%;
            height: 100%;
        }
        @media @media-md-down {
            &-container {
                flex-direction: column-reverse;
                align-items: center;
                height: auto;
            }
        }

        &-back-forward {
            display: none;
            margin-top: 16px;
        }
        @media @media-md-down {
            &-back-forward {
                display: block;
            }
        }

        &-loader {
            .transition-fade();

            position: absolute;
            top: 5px;
            right: 5px;
            bottom: 5px;
            left: 5px;
            z-index: 1;

            display: flex;
            align-items: center;
            justify-content: center;

            background-color: fade(@color-gray-lightest, 75%);
        }
    }
    @media @media-md-down {
        &__graph {
            height: auto;
        }
    }
}
</style>
